import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Divider,
    Switch,
} from "@mui/material";
import { Edit, Delete, Add, Lock } from "@mui/icons-material";
import "./RolesPermissions.css";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const MODULE_LABELS = {
    dashboard: "Dashboard",
    cyclone: "Cyclone",
    landslide: "Landslide",
    earthquake: "Earthquake",
    flood: "Flood",
    firerisk: "Fire Risk",
    roadaccident: "Road Accident",
    lightning: "Lightning",
    dissemination: "Dissemination",
    admin: "Administration",
};

// Module display order — mirrors the sidebar (Sidebar.js menuItems), so an
// admin scanning this list sees permissions in the same order they'd
// encounter the matching pages in the nav.
const MODULE_ORDER = [
    "dashboard", "cyclone", "landslide", "earthquake", "flood", "firerisk",
    "roadaccident", "lightning", "dissemination", "admin",
];

// Item order within each module — same source as Sidebar.js's subItems
// arrays. Any permission code not listed here (e.g. one added later and not
// yet wired into the sidebar) is appended after these, in whatever order
// the API returned it, so nothing silently disappears from the screen.
const PERMISSION_ORDER = {
    dashboard: ["dashboard_overview", "dashboard_active_events", "weather_station"],
    cyclone: [
        "cyclone_disturbance_track", "cyclone_ocean_state", "cyclone_historical_cyclone",
        "cyclone_advisory", "cyclone_report_list", "cyclone_data_panel_advisory",
        "cyclone_data_panel_felt_report",
        "cyclone_disturbance_track_generate_report",
    ],
    landslide: [
        "landslide_forecast", "landslide_projections", "landslide_advisory",
        "landslide_historical_data", "landslide_geological_terrain_land_cover",
        "landslide_geological_terrain_soil_texture", "landslide_susceptibility_lsm",
        "landslide_susceptibility_seasonal_wise", "landslide_report_list",
        "landslide_data_panel_felt_report", "landslide_data_panel_advisory",
        "landslide_forecast_generate_report",
    ],
    earthquake: [
        "earthquake_statistics", "earthquake_recent_earthquake", "earthquake_data_panel_advisory",
        "earthquake_advisory", "earthquake_felt_report", "earthquake_report_list",
        "earthquake_data_panel_events", "earthquake_data_panel_settings",
        "earthquake_exposure_health_centers",
        "earthquake_recent_earthquake_send_email", "earthquake_recent_earthquake_bulletin",
    ],
    flood: [
        "flood_flash_flood_threat", "flood_dashboard", "flood_analysis",
        "flood_exposure_hydrological_data", "flood_data_panel_advisory",
        "flood_advisory_view", "flood_data_panel_felt_report", "flood_report_list",
        "flood_flash_flood_threat_generate_advisory",
    ],
    firerisk: [
        "firerisk_forecast", "firerisk_firms", "firerisk_exposure_infrastructure", "firerisk_exposure_population",
        "firerisk_exposure_network", "firerisk_advisory", "firerisk_data_panel_felt_report",
        "firerisk_data_panel_advisory", "firerisk_report_list",
        "firerisk_forecast_generate_report",
    ],
    roadaccident: [
        "roadaccident_assistance_on_move", "roadaccident_hospitals", "roadaccident_road_networks",
        "roadaccident_data_panel_advisory", "roadaccident_advisory", "roadaccident_dashboard",
        "roadaccident_report_list", "roadaccident_data_panel_reports",
    ],
    lightning: [
        "lightning_nowcast", "lightning_data_panel_advisory", "lightning_advisory",
        "lightning_statistics", "lightning_report_list", "lightning_flash_density",
        "lightning_data_panel_reports",
        "lightning_flash_density_generate_report",
    ],
    dissemination: ["dissemination_broadcast", "dissemination_staff_info_form", "dissemination_staff_profile"],
    admin: [
        "admin_user_management", "admin_roles_permissions", "admin_data_management",
        "admin_forecast_value_addition", "admin_system_monitoring", "admin_situation_reports",
        "admin_report_approval",
    ],
};

const orderIndex = (order, code) => {
    const idx = order.indexOf(code);
    return idx === -1 ? order.length : idx;
};

function authHeaders() {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function slugifyCode(name) {
    return name
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "_")
        .replace(/(^_|_$)/g, "");
}

const EMPTY_FORM = { code: "", name: "", description: "", permission_codes: [] };

const RolesPermissions = () => {
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState("");
    // Permissions/All Permissions toggle in the edit-role permission list —
    // defaults to "Permissions" (locked/default-access items hidden) each
    // time the dialog opens; switching right reveals the locked ones too.
    const [showLocked, setShowLocked] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const [rolesRes, permsRes] = await Promise.all([
                fetch(`${backendUrl}login/roles/`, { headers: authHeaders() }),
                fetch(`${backendUrl}login/permissions/`, { headers: authHeaders() }),
            ]);
            const rolesData = await rolesRes.json();
            const permsData = await permsRes.json();
            if (rolesRes.ok) setRoles(Array.isArray(rolesData) ? rolesData : rolesData.results ?? []);
            if (permsRes.ok) setPermissions(Array.isArray(permsData) ? permsData : permsData.results ?? []);
        } catch (err) {
            console.error("Failed to load roles/permissions:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const groupedPermissions = useMemo(() => {
        const groups = {};
        permissions.forEach((p) => {
            if (!groups[p.module]) groups[p.module] = [];
            groups[p.module].push(p);
        });

        // Re-key in MODULE_ORDER (sidebar order) first, then append any
        // module the sidebar doesn't know about yet so nothing is hidden.
        const orderedGroups = {};
        const seenModules = new Set();
        [...MODULE_ORDER, ...Object.keys(groups)].forEach((module) => {
            if (seenModules.has(module) || !groups[module]) return;
            seenModules.add(module);
            const order = PERMISSION_ORDER[module] || [];
            orderedGroups[module] = [...groups[module]].sort(
                (a, b) => orderIndex(order, a.code) - orderIndex(order, b.code)
            );
        });
        return orderedGroups;
    }, [permissions]);

    const allPermissionCodes = useMemo(() => permissions.map((p) => p.code), [permissions]);
    // Default-access permissions are granted to every role by the backend, so
    // they always read as ticked here - even if this form's stored list (the
    // role's saved codes) predates a permission that was made default later.
    const isGranted = (p) => p.is_default_access || form.permission_codes.includes(p.code);
    const allChecked = permissions.length > 0 && permissions.every(isGranted);

    // Codes with is_default_access=True (from the API) — granted to every
    // role by the backend regardless of what's stored for it, so the form
    // always reflects them as selected. Read straight from live permission
    // data instead of a hardcoded list, so a DB-side change (e.g. via the
    // ALTER TABLE / UPDATE queries in login/models.py's docstring) shows up
    // here without a frontend deploy.
    const defaultAccessCodes = useMemo(
        () => new Set(permissions.filter((p) => p.is_default_access).map((p) => p.code)),
        [permissions]
    );
    const withDefaults = (codes) => Array.from(new Set([...codes, ...defaultAccessCodes]));

    // --- Form open/close ---
    const openCreate = () => {
        setIsEdit(false);
        setForm({ ...EMPTY_FORM, permission_codes: withDefaults([]) });
        setFormError("");
        setShowLocked(false);
        setFormOpen(true);
    };

    const openEdit = (role) => {
        setIsEdit(true);
        setForm({
            code: role.code,
            name: role.name,
            description: role.description || "",
            permission_codes: withDefaults(role.permission_codes || []),
        });
        setFormError("");
        setShowLocked(false);
        setFormOpen(true);
    };

    const handleNameChange = (value) => {
        setForm((f) => ({ ...f, name: value, code: isEdit ? f.code : slugifyCode(value) }));
    };

    const togglePermission = (code) => {
        if (defaultAccessCodes.has(code)) return; // locked — always granted, can't be unchecked
        setForm((f) => ({
            ...f,
            permission_codes: f.permission_codes.includes(code)
                ? f.permission_codes.filter((c) => c !== code)
                : [...f.permission_codes, code],
        }));
    };

    const toggleModule = (module, checked) => {
        const codes = (groupedPermissions[module] || []).map((p) => p.code);
        setForm((f) => ({
            ...f,
            permission_codes: checked
                ? Array.from(new Set([...f.permission_codes, ...codes]))
                // Unchecking a module never drops its default-access codes.
                : f.permission_codes.filter((c) => !codes.includes(c) || defaultAccessCodes.has(c)),
        }));
    };

    const toggleAll = (checked) => {
        setForm((f) => ({ ...f, permission_codes: checked ? [...allPermissionCodes] : withDefaults([]) }));
    };

    const handleSave = async () => {
        setFormError("");
        if (!form.name.trim()) { setFormError("Role name is required."); return; }
        if (!form.code.trim()) { setFormError("Role code is required."); return; }

        setSaving(true);
        try {
            const url = isEdit ? `${backendUrl}login/roles/${form.code}/` : `${backendUrl}login/roles/`;
            const method = isEdit ? "PATCH" : "POST";
            const response = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({
                    code: form.code,
                    name: form.name,
                    description: form.description,
                    permission_codes: form.permission_codes,
                }),
            });
            const data = await response.json();
            if (response.ok) {
                setFormOpen(false);
                loadData();
            } else {
                const rawError = data.code || data.name || data.detail || "Failed to save role. Please try again.";
                setFormError(Array.isArray(rawError) ? rawError.join(" ") : rawError);
            }
        } catch (err) {
            setFormError(`Failed to connect to the server: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    // --- Delete ---
    const handleDeleteConfirm = async () => {
        setDeleteLoading(true);
        setDeleteError("");
        try {
            const response = await fetch(`${backendUrl}login/roles/${deleteTarget.code}/`, {
                method: "DELETE",
                headers: authHeaders(),
            });
            if (response.ok) {
                setDeleteTarget(null);
                loadData();
            } else {
                const data = await response.json().catch(() => ({}));
                setDeleteError(data.detail || "Failed to delete role.");
            }
        } catch (err) {
            setDeleteError(`Failed to connect to the server: ${err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: "name",
                header: "Role Name",
                size: 200,
                Cell: ({ row }) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <span style={{ fontWeight: 600, color: "#1e293b" }}>{row.original.name}</span>
                        {row.original.is_system && (
                            <Tooltip title="Built-in system role" arrow>
                                <Lock sx={{ fontSize: 14, color: "#94a3b8" }} />
                            </Tooltip>
                        )}
                    </Box>
                ),
            },
            {
                accessorKey: "description",
                header: "Description",
                size: 260,
                Cell: ({ cell }) => <span style={{ color: "#64748b", fontSize: "13px" }}>{cell.getValue()}</span>,
            },
            {
                accessorKey: "permission_codes",
                header: "Permissions",
                size: 110,
                Cell: ({ cell }) => <span style={{ color: "#475569" }}>{(cell.getValue() || []).length}</span>,
            },
            {
                accessorKey: "user_count",
                header: "Users",
                size: 90,
                Cell: ({ cell }) => <span style={{ color: "#475569" }}>{cell.getValue()}</span>,
            },
            {
                id: "action",
                header: "Action",
                size: 110,
                Cell: ({ row }) => (
                    <Box sx={{ display: "flex", gap: "4px" }}>
                        <Tooltip title="Edit Role" arrow>
                            <IconButton size="small" onClick={() => openEdit(row.original)} sx={{ color: "#2563eb", "&:hover": { backgroundColor: "#2563eb14" } }}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={row.original.is_system ? "Built-in roles can't be deleted" : "Delete Role"} arrow>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={row.original.is_system}
                                    onClick={() => { setDeleteTarget(row.original); setDeleteError(""); }}
                                    sx={{ color: "#ef4444", "&:hover": { backgroundColor: "#ef444414" } }}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <div className="roles-permissions-container">
            <div className="roles-permissions-header">
                <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#1e293b" }}>Roles & Permissions</Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openCreate}
                    sx={{
                        backgroundColor: "#2563eb",
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "8px",
                        px: 2.5,
                        py: 1,
                        fontSize: "13px",
                        boxShadow: "0 2px 8px rgba(37, 99, 235, 0.25)",
                        "&:hover": { backgroundColor: "#1d4ed8" },
                    }}
                >
                    New Role
                </Button>
            </div>

            <Box sx={{ mt: 2 }}>
                <MaterialReactTable
                    columns={columns}
                    data={roles}
                    state={{ isLoading: loading }}
                    enableColumnActions={false}
                    enableSorting
                    enablePagination
                    enableDensityToggle={false}
                    enableFullScreenToggle={false}
                    initialState={{ density: "comfortable", pagination: { pageSize: 10, pageIndex: 0 } }}
                    muiTableContainerProps={{ sx: { maxHeight: "calc(100vh - 260px)", borderRadius: "12px" } }}
                    muiTableHeadCellProps={{
                        sx: {
                            fontWeight: 700,
                            backgroundColor: "#f8fafc",
                            color: "#475569",
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            borderBottom: "2px solid #e2e8f0",
                        },
                    }}
                    muiTableBodyCellProps={{ sx: { fontSize: "13px", borderBottom: "1px solid #f1f5f9" } }}
                />
            </Box>

            {/* Create / Edit Role Dialog */}
            <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: "18px", color: "#1e293b", pb: 1 }}>
                    {isEdit ? `Edit Role — ${form.name}` : "New Role"}
                </DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "12px !important" }}>
                    <TextField
                        label="Role Name"
                        value={form.name}
                        onChange={(e) => handleNameChange(e.target.value)}
                        fullWidth size="small" variant="outlined"
                    />
                    <TextField
                        label="Description"
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        fullWidth size="small" variant="outlined" multiline minRows={2}
                    />

                    <Divider />

                    <Box sx={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                        <Box sx={{ borderBottom: "1px solid #f1f5f9", px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>Permissions</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <FormControlLabel
                                    control={<Checkbox size="small" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} />}
                                    label={<Typography sx={{ fontSize: "13px" }}>Grant all permissions</Typography>}
                                />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <Typography sx={{ fontSize: "12px", fontWeight: showLocked ? 400 : 700, color: showLocked ? "#94a3b8" : "#2563eb" }}>
                                        Permissions
                                    </Typography>
                                    <Switch
                                        size="small"
                                        checked={showLocked}
                                        onChange={(e) => setShowLocked(e.target.checked)}
                                    />
                                    <Typography sx={{ fontSize: "12px", fontWeight: showLocked ? 700 : 400, color: showLocked ? "#2563eb" : "#94a3b8" }}>
                                        All Permissions
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ maxHeight: "360px", overflowY: "auto" }}>
                            {Object.keys(groupedPermissions).map((module) => {
                                const items = groupedPermissions[module];
                                const moduleChecked = items.every(isGranted);
                                const moduleFullyDefault = items.every((p) => p.is_default_access);
                                // Locked/default-access items lead the row, in
                                // their own line, so an admin can scan "what's
                                // fixed" before "what's togglable" — each
                                // subset keeps the sidebar-mirrored order from
                                // groupedPermissions.
                                const defaultItems = items.filter((p) => p.is_default_access);
                                const otherItems = items.filter((p) => !p.is_default_access);
                                const renderPermission = (p) => {
                                    const isDefault = p.is_default_access;
                                    const checkboxEl = (
                                        <Checkbox
                                            size="small"
                                            checked={isGranted(p)}
                                            disabled={isDefault}
                                            onChange={() => togglePermission(p.code)}
                                        />
                                    );
                                    const labelEl = (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Typography sx={{ fontSize: "13px", color: isDefault ? "#94a3b8" : "#475569" }}>{p.name}</Typography>
                                            {isDefault && <Lock sx={{ fontSize: 12, color: "#cbd5e1" }} />}
                                        </Box>
                                    );
                                    return (
                                        <FormControlLabel
                                            key={p.code}
                                            control={
                                                isDefault ? (
                                                    <Tooltip title="Default access — granted to every role, can't be unselected" arrow>
                                                        <span>{checkboxEl}</span>
                                                    </Tooltip>
                                                ) : checkboxEl
                                            }
                                            label={labelEl}
                                        />
                                    );
                                };
                                return (
                                    <Box key={module} sx={{ borderBottom: "1px solid #f1f5f9", px: 2, py: 1.5 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={moduleChecked}
                                                    disabled={moduleFullyDefault}
                                                    onChange={(e) => toggleModule(module, e.target.checked)}
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>{MODULE_LABELS[module] || module}</Typography>
                                                    {moduleFullyDefault && (
                                                        <Tooltip title="Every item in this module is default access — granted to all roles" arrow>
                                                            <Lock sx={{ fontSize: 13, color: "#94a3b8" }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            }
                                        />
                                        {module === "dashboard" && (
                                            <Typography sx={{ fontSize: "12px", color: "#94a3b8", pl: 4, mt: -0.5, mb: 0.5 }}>
                                                Forecast (the landing page) has no separate permission — it's open to every signed-in user.
                                            </Typography>
                                        )}
                                        {showLocked && defaultItems.length > 0 && (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, pl: 4 }}>
                                                {defaultItems.map(renderPermission)}
                                            </Box>
                                        )}
                                        {otherItems.length > 0 && (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, pl: 4, mt: showLocked && defaultItems.length > 0 ? 1.5 : 0 }}>
                                                {otherItems.map(renderPermission)}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    {formError && <Typography sx={{ color: "#ef4444", fontSize: "13px" }}>{formError}</Typography>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} disabled={saving} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        sx={{ backgroundColor: "#2563eb", textTransform: "none", fontWeight: 600, borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#1d4ed8" }, minWidth: "120px" }}
                    >
                        {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? "Save Changes" : "Create Role"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={Boolean(deleteTarget)} onClose={() => !deleteLoading && setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: "18px", color: "#1e293b", pb: 1 }}>Delete Role</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "#475569", fontSize: "14px" }}>
                        Are you sure you want to delete <strong style={{ color: "#1e293b" }}>{deleteTarget?.name}</strong>?
                        {deleteTarget?.user_count > 0 && (
                            <> It is currently assigned to <strong>{deleteTarget.user_count}</strong> user(s) — reassign them first.</>
                        )}
                    </Typography>
                    {deleteError && <Typography sx={{ color: "#ef4444", fontSize: "13px", mt: 1 }}>{deleteError}</Typography>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeleteConfirm}
                        disabled={deleteLoading}
                        sx={{ backgroundColor: "#ef4444", textTransform: "none", fontWeight: 600, borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#dc2626" }, minWidth: "90px" }}
                    >
                        {deleteLoading ? <CircularProgress size={18} color="inherit" /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default RolesPermissions;
